import React, { useEffect, useRef } from 'react';
import { Animated, Pressable, Text, View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { Paprika } from '../../../styles/theme';
import { DietDot } from '../../weekly/v2/primitives/DietDot';
import { ShimmerBox } from '../../weekly/v2/primitives/ShimmerBox';
import { CardMeta } from '../../weekly/v2/primitives/CardMeta';
import type { DietaryRole } from '../../../redux/mealPlanSlice';
import { useTranslation } from 'react-i18next';
import { RemoteImage } from '../../shared/RemoteImage';

interface Props {
  title: string;
  image?: string | null;
  portions: number;
  minutes?: number | null;
  dietaryRole?: DietaryRole;
  source?: string | null;
  rating?: number | null;
  ratingCount?: number | null;
  shown: boolean;
  onShuffle: () => void;
  onPortionsTap: () => void;
  onCardPress?: () => void;
}

export function SuggCard({ title, image, portions, minutes, dietaryRole, source, rating, shown, onShuffle, onPortionsTap, onCardPress }: Props) {
  const { t } = useTranslation();
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(16)).current;
  const scale = useRef(new Animated.Value(0.97)).current;

  useEffect(() => {
    if (shown) {
      Animated.parallel([
        Animated.timing(opacity, { toValue: 1, duration: 500, useNativeDriver: true }),
        Animated.timing(translateY, { toValue: 0, duration: 500, useNativeDriver: true }),
        Animated.timing(scale, { toValue: 1, duration: 500, useNativeDriver: true }),
      ]).start();
    }
  }, [shown]);

  if (!shown) return <View style={styles.hidden} />;

  return (
    <Pressable onPress={onCardPress}>
    <Animated.View style={[styles.card, { opacity, transform: [{ translateY }, { scale }] }]}>
      <View style={styles.photoWrap}>
        <RemoteImage
          uri={image}
          variant="thumb"
          style={styles.photo}
          resizeMode="cover"
          fallback={<ShimmerBox width="100%" height={152} borderRadius={0} />}
        />

        {/* DietDot pill – top left — only render when a dietary role is set */}
        {dietaryRole && (
          <View style={styles.dietPill}>
            <DietDot role={dietaryRole} size={8} />
          </View>
        )}

        {/* Portions badge – top right (people · count · edit pencil) */}
        <Pressable style={styles.portionsBadge} onPress={onPortionsTap}>
          <Feather name="users" size={12} color={Paprika.textSecondary} />
          <Text style={styles.portionsText}>{portions}</Text>
          <Feather name="edit-2" size={11} color={Paprika.textMuted} />
        </Pressable>

        {/* Shuffle button – bottom right */}
        <Pressable style={styles.shuffleBtn} onPress={onShuffle}>
          <Feather name="refresh-cw" size={12} color={Paprika.primary} />
          <Text style={styles.shuffleBtnText}>{t('mealConfig.anotherRecipe')}</Text>
        </Pressable>
      </View>

      <View style={styles.info}>
        <Text style={styles.title} numberOfLines={2}>{title}</Text>
        <CardMeta sourceUrl={source} rating={rating} minutes={minutes} />
      </View>
    </Animated.View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  hidden: {
    height: 268,
  },
  card: {
    height: 268,
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: 10,
    overflow: 'hidden',
    shadowColor: 'rgba(82, 68, 60, 0.08)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 4,
  },
  photoWrap: {
    height: 152,
    position: 'relative',
    backgroundColor: Paprika.surfaceLow,
  },
  photo: {
    width: '100%' as any,
    height: '100%' as any,
  },
  dietPill: {
    position: 'absolute',
    top: 8,
    left: 8,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.92)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  portionsBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    backgroundColor: 'rgba(255,255,255,0.92)',
    borderRadius: 100,
    paddingHorizontal: 9,
    paddingVertical: 4,
  },
  portionsText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 12,
    color: Paprika.textPrimary,
  },
  shuffleBtn: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    backgroundColor: 'rgba(255,255,255,0.92)',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  shuffleBtnText: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.primary,
  },
  info: {
    flex: 1,
    padding: 10,
    gap: 4,
    justifyContent: 'space-between',
  },
  title: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 14,
    lineHeight: 19,
    color: Paprika.textPrimary,
  },
});
